import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { WalletIcon, AlertTriangleIcon } from 'lucide-react';
import SwapInterface from './components/SwapInterface';
import ErrorBoundary from './components/ErrorBoundary';
import AdminPage from './AdminPage';
import CLIPage from './CLIPage';
import V2Page from './V2Page';
import FountainPage from './FountainPage';
import { useSwapper } from './hooks/useSwapper';
import { formatAddress } from './utils/helpers';
import web3Provider from './utils/web3Provider';
import { TokenLogos } from './assets/tokenLogos';
import './index.css';

function App() {
  const [account, setAccount] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [networkError, setNetworkError] = useState(null);

  // Check which page is requested
  const isAdminPage = window.location.pathname === '/admin' || window.location.search.includes('admin=true');
  const isCLIPage = window.location.pathname === '/cli';
  const isV1Page = window.location.pathname === '/v1';
  const isFountainPage = window.location.pathname === '/fountain';

  const { executeSwap, isSwapping, resetSwapError } = useSwapper();

  useEffect(() => {
    // Check if already connected
    const checkConnection = async () => {
      try {
        if (window.ethereum && window.ethereum.selectedAddress) {
          const connectionResult = await web3Provider.connect();
          setAccount(connectionResult.account);
          
          if (connectionResult.chainId !== 250) {
            setNetworkError('Please switch to Fantom network');
          } else {
            setNetworkError(null); // Clear network error if on correct network
          }
        }
      } catch (error) {
        console.error('Auto-connection failed:', error);
      }
    };

    checkConnection();

    // Listen for account changes
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          setAccount(null);
          setConnectionError(null);
          setNetworkError(null);
        } else {
          setAccount(accounts[0]);
        }
      });

      window.ethereum.on('chainChanged', (chainId) => {
        const numericChainId = parseInt(chainId, 16);
        if (numericChainId !== 250) {
          setNetworkError('Please switch to Fantom network');
        } else {
          setNetworkError(null);
        }
        window.location.reload();
      });
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeAllListeners('accountsChanged');
        window.ethereum.removeAllListeners('chainChanged');
      }
    };
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    setConnectionError(null);
    setNetworkError(null);

    try {
      const result = await web3Provider.connect();
      setAccount(result.account);
      
      if (result.chainId !== 250) {
        setNetworkError('Please switch to Fantom network');
      } else {
        setNetworkError(null); // Clear network error if on correct network
      }
    } catch (error) {
      console.error('Connection error:', error);
      setConnectionError(error.message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    web3Provider.disconnect();
    setAccount(null);
    setConnectionError(null);
    setNetworkError(null);
  };

  const handleSwap = async (swapParams) => {
    resetSwapError();
    await executeSwap(swapParams);
  };

  // If admin page is requested, show admin interface
  if (isAdminPage) {
    return <AdminPage />;
  }

  // If CLI page is requested, show CLI documentation
  if (isCLIPage) {
    return <CLIPage />;
  }

  // If Fountain page is requested, show Fountain staking interface
  if (isFountainPage) {
    return <FountainPage />;
  }

  // If V1 page is requested, show V1 interface
  if (isV1Page) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-950 to-black relative overflow-hidden">
        {/* Background Animation */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 right-20 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <main className="relative z-10">
          <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-6">
                <img src={TokenLogos.GIGACHAD} alt="Chad Swap" className="w-16 h-16 mr-4" />
                <h1 className="text-6xl font-bold text-white font-orbitron">
                  ChadSwap V1
                </h1>
              </div>
              <p className="text-xl text-blue-200 font-rajdhani">
                Trade LIF3 & LSHARE with zero slippage
              </p>
              <div className="mt-4">
                <a 
                  href="/"
                  className="text-blue-400 hover:text-blue-300 underline text-sm"
                >
                  ← Back to V2 (Multi-DEX)
                </a>
              </div>
            </div>

            {/* Swap Interface */}
            <div className="max-w-md mx-auto">
              <ErrorBoundary>
                <SwapInterface
                  account={account}
                  signer={web3Provider.getSigner()}
                  onConnect={handleConnect}
                  onSwap={handleSwap}
                  isSwapping={isSwapping}
                />
              </ErrorBoundary>
            </div>

            {/* Supported Tokens */}
            <div className="mt-16 text-center">
              <h3 className="text-2xl font-bold text-white mb-8 font-orbitron">Supported Tokens</h3>
              <div className="flex justify-center items-center space-x-8">
                <div className="flex flex-col items-center space-y-2">
                  <img src={TokenLogos.FTM} alt="FTM" className="w-12 h-12" />
                  <span className="text-white text-sm font-medium">FTM</span>
                </div>
                <div className="flex flex-col items-center space-y-2">
                  <img src={TokenLogos.WFTM} alt="WFTM" className="w-12 h-12" />
                  <span className="text-white text-sm font-medium">WFTM</span>
                </div>
                <div className="flex flex-col items-center space-y-2">
                  <img src={TokenLogos.LIF3} alt="LIF3" className="w-12 h-12" />
                  <span className="text-white text-sm font-medium">LIF3</span>
                </div>
                <div className="flex flex-col items-center space-y-2">
                  <img src={TokenLogos.LSHARE} alt="LSHARE" className="w-12 h-12" />
                  <span className="text-white text-sm font-medium">LSHARE</span>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Toast Notifications */}
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 5000,
            style: {
              background: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: 'white',
              },
            },
            error: {
              iconTheme: {
                primary: '#EF4444',
                secondary: 'white',
              },
            },
          }}
        />
      </div>
    );
  }

  // Default: Show V2 interface
  return (
    <>
      <V2Page account={account} onConnect={handleConnect} />
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: 'white',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: 'white',
            },
          },
        }}
      />
    </>
  );
}
export default App;
